import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { api, API, formatApiErrorDetail, STATUS_LABELS, STATUS_COLORS, SERVICE_OPTIONS } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { RefreshCw, X, FileDown, Image as ImageIcon } from "lucide-react";

const SERVICE_MAP = Object.fromEntries(SERVICE_OPTIONS.map((s) => [s.value, s.label]));

export default function AdminDashboard() {
  const { user } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const [b, s] = await Promise.all([api.get("/bookings"), api.get("/bookings-stats")]);
      setBookings(b.data);
      setStats(s.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (user && user !== false) load();
  }, [user]);

  if (user === null) return <div className="p-24 text-center text-zinc-400">Loading...</div>;
  if (user === false) return <Navigate to="/admin/login" replace />;

  const filtered = filter === "all" ? bookings : bookings.filter((b) => b.status === filter);

  async function updateStatus(id, newStatus, notes) {
    setSaving(true);
    try {
      const { data } = await api.patch(`/bookings/${id}`, { status: newStatus, admin_notes: notes });
      setBookings((prev) => prev.map((b) => (b.id === id ? data : b)));
      if (selected?.id === id) setSelected(data);
      toast.success("Booking updated.");
      const s = await api.get("/bookings-stats");
      setStats(s.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Update failed");
    } finally {
      setSaving(false);
    }
  }

  async function downloadPdf(id, reference) {
    try {
      const res = await api.get(`/bookings/${id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `KPMX-${reference}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Could not download job sheet");
    }
  }

  return (
    <div data-testid="admin-dashboard" className="max-w-7xl mx-auto px-4 sm:px-8 py-12">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-10">
        <div>
          <span className="font-mono text-xs uppercase tracking-widest text-orange-500">— Dashboard</span>
          <h1 className="font-display text-4xl sm:text-5xl uppercase mt-2">Bookings</h1>
        </div>
        <button onClick={load} disabled={loading} data-testid="refresh-btn"
          className="inline-flex items-center gap-2 border border-white/20 hover:border-orange-500 text-white px-4 py-2 font-mono text-xs uppercase tracking-widest">
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-8">
        <StatCard label="Total" value={stats.total || 0} active={filter === "all"} onClick={() => setFilter("all")} testId="stat-total" />
        {Object.keys(STATUS_LABELS).map((k) => (
          <StatCard key={k} label={STATUS_LABELS[k]} value={stats[k] || 0} active={filter === k} onClick={() => setFilter(k)} testId={`stat-${k}`} />
        ))}
      </div>

      <div className="border border-white/10 overflow-x-auto">
        <table className="w-full font-mono text-sm">
          <thead className="bg-zinc-900 border-b border-white/10">
            <tr className="text-left">
              <th className="p-3 uppercase text-xs text-zinc-400">Ref</th>
              <th className="p-3 uppercase text-xs text-zinc-400">Customer</th>
              <th className="p-3 uppercase text-xs text-zinc-400 hidden md:table-cell">Service</th>
              <th className="p-3 uppercase text-xs text-zinc-400 hidden lg:table-cell">Bike</th>
              <th className="p-3 uppercase text-xs text-zinc-400">Status</th>
              <th className="p-3 uppercase text-xs text-zinc-400 hidden md:table-cell">Created</th>
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={7} className="p-10 text-center text-zinc-500">No bookings yet.</td></tr>
            )}
            {filtered.map((b) => (
              <tr key={b.id} data-testid={`booking-row-${b.reference}`} className="border-b border-white/5 hover:bg-zinc-900/60">
                <td className="p-3 text-orange-500">
                  {b.reference}
                  {b.photos?.length > 0 && (
                    <span className="ml-2 inline-flex items-center gap-1 text-xs text-zinc-500"><ImageIcon size={10} /> {b.photos.length}</span>
                  )}
                </td>
                <td className="p-3">{b.customer_name}<div className="text-xs text-zinc-500">{b.customer_email}</div></td>
                <td className="p-3 hidden md:table-cell text-zinc-300">{SERVICE_MAP[b.service_type]}</td>
                <td className="p-3 hidden lg:table-cell text-zinc-300">{b.bike_make} {b.bike_model} {b.bike_year}</td>
                <td className="p-3">
                  <span className={`px-2 py-1 text-xs border ${STATUS_COLORS[b.status]}`}>{STATUS_LABELS[b.status]}</span>
                </td>
                <td className="p-3 hidden md:table-cell text-zinc-500 text-xs">{new Date(b.created_at).toLocaleDateString()}</td>
                <td className="p-3 flex items-center gap-3 justify-end">
                  <button onClick={() => downloadPdf(b.id, b.reference)} data-testid={`pdf-btn-${b.reference}`}
                    className="text-xs uppercase tracking-widest text-zinc-400 hover:text-orange-500 inline-flex items-center gap-1">
                    <FileDown size={12} /> PDF
                  </button>
                  <button onClick={() => setSelected(b)} data-testid={`view-btn-${b.reference}`}
                    className="text-xs uppercase tracking-widest text-orange-500 hover:text-orange-400">View</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selected && (
        <BookingDrawer booking={selected} onClose={() => setSelected(null)} onSave={updateStatus} saving={saving} onDownloadPdf={downloadPdf} />
      )}
    </div>
  );
}

function StatCard({ label, value, active, onClick, testId }) {
  return (
    <button onClick={onClick} data-testid={testId}
      className={`border p-4 text-left transition-colors ${
        active ? "border-orange-500 bg-orange-600/10" : "border-white/10 bg-zinc-900/40 hover:border-white/30"
      }`}>
      <div className="font-mono text-xs uppercase tracking-widest text-zinc-400">{label}</div>
      <div className="font-display text-3xl mt-1">{value}</div>
    </button>
  );
}

function PhotoTile({ path }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let alive = true;
    let objectUrl;
    (async () => {
      try {
        const res = await api.get(`/files/${path}`, { responseType: "blob" });
        objectUrl = URL.createObjectURL(res.data);
        if (alive) setSrc(objectUrl);
      } catch { /* ignore */ }
    })();
    return () => { alive = false; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [path]);
  return (
    <a href={src || "#"} target="_blank" rel="noopener noreferrer"
      className="block aspect-square bg-zinc-950 border border-white/10 overflow-hidden hover:border-orange-500">
      {src ? <img src={src} alt="" className="w-full h-full object-cover" /> :
        <div className="w-full h-full flex items-center justify-center text-zinc-600 text-xs">Loading…</div>}
    </a>
  );
}

function BookingDrawer({ booking, onClose, onSave, saving, onDownloadPdf }) {
  const [status, setStatus] = useState(booking.status);
  const [notes, setNotes] = useState(booking.admin_notes || "");

  useEffect(() => {
    setStatus(booking.status);
    setNotes(booking.admin_notes || "");
  }, [booking]);

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="booking-drawer">
      <div className="flex-1 bg-black/70" onClick={onClose} />
      <div className="w-full max-w-xl bg-zinc-950 border-l border-white/10 overflow-y-auto">
        <div className="flex items-start justify-between p-6 border-b border-white/10 sticky top-0 bg-zinc-950">
          <div>
            <div className="font-mono text-xs uppercase text-zinc-500">Booking</div>
            <div className="font-display text-2xl text-orange-500">{booking.reference}</div>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => onDownloadPdf(booking.id, booking.reference)} data-testid="drawer-pdf"
              className="inline-flex items-center gap-1 text-xs uppercase tracking-widest text-zinc-400 hover:text-orange-500">
              <FileDown size={14} /> PDF
            </button>
            <button onClick={onClose} data-testid="drawer-close" className="text-zinc-400 hover:text-white p-1">
              <X size={20} />
            </button>
          </div>
        </div>
        <div className="p-6 space-y-5 text-sm">
          <DetailRow label="Customer" value={booking.customer_name} />
          <DetailRow label="Email" value={booking.customer_email} />
          <DetailRow label="Phone" value={booking.customer_phone} />
          <DetailRow label="Service" value={SERVICE_MAP[booking.service_type]} />
          <DetailRow label="Collection" value={booking.collection_method === "courier_box" ? "Courier box" : "We collect"} />
          <DetailRow label="Bike" value={`${booking.bike_make} ${booking.bike_model} ${booking.bike_year}`} />
          {booking.bearing_types?.length > 0 && (
            <DetailRow label="Bearings" value={booking.bearing_types.join(", ")} />
          )}
          <DetailRow label="Address"
            value={`${booking.address_line1}${booking.address_line2 ? ", " + booking.address_line2 : ""}, ${booking.city} ${booking.postcode}, ${booking.country}`} />
          {booking.notes && <DetailRow label="Customer notes" value={booking.notes} />}
          <DetailRow label="Created" value={new Date(booking.created_at).toLocaleString()} />

          {booking.photos?.length > 0 && (
            <div className="border-t border-white/10 pt-5">
              <div className="font-mono text-xs uppercase tracking-widest text-zinc-400 mb-3">
                Photos ({booking.photos.length})
              </div>
              <div className="grid grid-cols-3 gap-2" data-testid="drawer-photos">
                {booking.photos.map((p) => (
                  <PhotoTile key={p.id} path={p.path} />
                ))}
              </div>
            </div>
          )}

          <div className="border-t border-white/10 pt-5">
            <label className="font-mono text-xs uppercase tracking-widest text-zinc-400">Status</label>
            <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="drawer-status-select"
              className="mt-2 w-full bg-zinc-950 border border-white/10 focus:border-orange-500 outline-none px-3 py-3 text-white">
              {Object.keys(STATUS_LABELS).map((k) => (
                <option key={k} value={k}>{STATUS_LABELS[k]}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="font-mono text-xs uppercase tracking-widest text-zinc-400">Workshop notes</label>
            <textarea rows={4} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="drawer-notes"
              className="mt-2 w-full bg-zinc-950 border border-white/10 focus:border-orange-500 outline-none px-3 py-3 text-white" />
          </div>
          <button onClick={() => onSave(booking.id, status, notes)} disabled={saving} data-testid="drawer-save"
            className="btn-hard w-full bg-orange-600 hover:bg-orange-500 text-white px-6 py-3 font-display uppercase tracking-widest text-sm disabled:opacity-50">
            {saving ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

function DetailRow({ label, value }) {
  return (
    <div className="grid grid-cols-3 gap-3">
      <div className="font-mono text-xs uppercase tracking-widest text-zinc-500">{label}</div>
      <div className="col-span-2 text-white break-words">{value}</div>
    </div>
  );
}
